import React, { useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiMail,
  FiPackage,
  FiRefreshCw,
  FiX,
} from 'react-icons/fi';
import request from '../utils/request';
import { formatDate } from '../utils/formatDate';

function currency(value) {
  return (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

export default function ReportedMissingProductsQueue({
  orders = [],
  loading = false,
  error = '',
  onRetry,
  onOrderUpdated,
}) {
  const [tab, setTab] = useState('pending');
  const [processingKey, setProcessingKey] = useState('');
  const [actionError, setActionError] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const queueOrders = useMemo(
    () =>
      orders.map((order) => {
        const reports = order.reportedMissingProducts || [];
        const refundedCount = reports.filter(
          (report) => report.product?.refunded === true
        ).length;
        return {
          ...order,
          reportCount: reports.length,
          refundedCount,
          pendingCount: reports.length - refundedCount,
        };
      }),
    [orders]
  );

  const pendingOrders = useMemo(
    () => queueOrders.filter((order) => order.pendingCount > 0),
    [queueOrders]
  );
  const refundedOrders = useMemo(
    () =>
      queueOrders.filter(
        (order) => order.reportCount > 0 && order.pendingCount === 0
      ),
    [queueOrders]
  );
  const visibleOrders = tab === 'pending' ? pendingOrders : refundedOrders;
  const selectedOrder = queueOrders.find(
    (order) => String(order._id) === String(selectedOrderId)
  );

  const markRefunded = async (row) => {
    if (!row.product?._id) return;
    const key = `${row.order._id}-${row.product._id}`;
    setProcessingKey(key);
    setActionError('');
    try {
      const { data } = await request.patch(
        `/orders/${row.order._id}/reported-missing-products/${row.product._id}/refunded`
      );
      if (data?.order && onOrderUpdated) onOrderUpdated(data.order);
      return true;
    } catch (err) {
      setActionError(
        err?.response?.data?.message || 'Unable to mark this product refunded.'
      );
      return false;
    } finally {
      setProcessingKey('');
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">
            <FiAlertCircle className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Driver-Reported Missing Products
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Mark an item after its refund has been issued. No approval is required.
            </p>
          </div>
        </div>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        ) : null}
      </div>

      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50/60 px-5 py-3 dark:border-gray-700 dark:bg-gray-800/40">
        {[
          { id: 'pending', label: 'Needs refund', count: pendingOrders.length },
          { id: 'refunded', label: 'Refunded', count: refundedOrders.length },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold ${
              tab === item.id
                ? item.id === 'pending'
                  ? 'bg-rose-500 text-white'
                  : 'bg-emerald-500 text-white'
                : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            {item.label}
            <span className={`rounded-full px-1.5 text-[10px] ${tab === item.id ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
              {item.count}
            </span>
          </button>
        ))}
      </div>

      {error || actionError ? (
        <div className="border-b border-red-200 bg-red-50 px-5 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          {actionError || error}
        </div>
      ) : null}

      {loading && queueOrders.length === 0 ? (
        <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-gray-500 dark:text-gray-400">
          <FiRefreshCw className="h-4 w-4 animate-spin" />
          Loading missing products…
        </div>
      ) : visibleOrders.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <FiPackage className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {tab === 'pending'
              ? 'No driver-reported products need a refund.'
              : 'No driver-reported products have been marked refunded.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {visibleOrders.map((order) => {
            const missingNames = (order.reportedMissingProducts || [])
              .map((report) => report.product?.name)
              .filter(Boolean);
            return (
              <button
                type="button"
                key={order._id}
                onClick={() => setSelectedOrderId(order._id)}
                className="flex w-full flex-col gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 lg:flex-row lg:items-center"
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                    <FiPackage className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        Order #{order.uniqueId || String(order._id).slice(-6)}
                      </span>
                      {order.pendingCount === 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          <FiCheckCircle className="h-3 w-3" /> All reported items refunded
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                          <FiClock className="h-3 w-3" /> {order.pendingCount} need refund
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {order.inStockDate ? formatDate(order.inStockDate) : 'No delivery date'}
                      {` · ${order.products?.length || 0} total item${order.products?.length === 1 ? '' : 's'}`}
                      {` · ${order.reportCount} reported missing`}
                    </div>
                    <div className="mt-1 truncate text-xs text-gray-600 dark:text-gray-300">
                      {missingNames.length > 0
                        ? missingNames.join(', ')
                        : 'Reported product no longer found on order'}
                    </div>
                  </div>
                </div>

                <div className="flex flex-shrink-0 items-center gap-2 text-xs font-semibold text-sky-700 dark:text-sky-300">
                  Open order workflow
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedOrder ? (
        <OrderRefundWorkflowModal
          key={selectedOrder._id}
          order={selectedOrder}
          processingKey={processingKey}
          onMarkRefunded={markRefunded}
          onClose={() => setSelectedOrderId(null)}
        />
      ) : null}
    </div>
  );
}

function OrderRefundWorkflowModal({
  order,
  processingKey,
  onMarkRefunded,
  onClose,
}) {
  const reportedById = useMemo(
    () =>
      new Map(
        (order.reportedMissingProducts || []).map((report) => [
          String(report.productId),
          report,
        ])
      ),
    [order.reportedMissingProducts]
  );
  const reportedProducts = useMemo(
    () =>
      (order.products || []).filter((product) =>
        reportedById.has(String(product._id))
      ),
    [order.products, reportedById]
  );
  const [selectedProductIds, setSelectedProductIds] = useState(
    () => new Set(reportedProducts.map((product) => String(product._id)))
  );
  const [selectedRefundProductIds, setSelectedRefundProductIds] = useState(
    () => new Set()
  );
  const [bulkMarking, setBulkMarking] = useState(false);
  const [bulkResult, setBulkResult] = useState('');
  const [subject, setSubject] = useState(
    `Bellhopt Order #${order.uniqueId} - Refund Processed`
  );
  const [message, setMessage] = useState(
    'We’re sincerely sorry that the items below were unavailable during fulfillment of your Bellhopt order. We have processed a refund for these products back to your original payment method.\n\nWe understand how disappointing it is when part of an order cannot be fulfilled, especially while you’re traveling. Thank you for your patience and understanding. If there is anything else we can help with, please reply to this email and our team will be happy to assist.'
  );
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sentMessage, setSentMessage] = useState('');

  const selectedProducts = reportedProducts.filter((product) =>
    selectedProductIds.has(String(product._id))
  );
  const selectedTotal = selectedProducts.reduce(
    (sum, product) =>
      sum + (Number(product.price) || 0) * (Number(product.quantity) || 1),
    0
  );
  const pendingReportedProducts = reportedProducts.filter(
    (product) => product.refunded !== true
  );
  const allPendingSelected =
    pendingReportedProducts.length > 0 &&
    pendingReportedProducts.every((product) =>
      selectedRefundProductIds.has(String(product._id))
    );

  const toggleRefundProduct = (productId) => {
    setSelectedRefundProductIds((current) => {
      const next = new Set(current);
      const key = String(productId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setBulkResult('');
  };

  const toggleAllPendingRefunds = () => {
    setSelectedRefundProductIds(
      allPendingSelected
        ? new Set()
        : new Set(pendingReportedProducts.map((product) => String(product._id)))
    );
    setBulkResult('');
  };

  const markSelectedRefunded = async () => {
    const selectedPending = pendingReportedProducts.filter((product) =>
      selectedRefundProductIds.has(String(product._id))
    );
    if (selectedPending.length === 0) return;

    setBulkMarking(true);
    setBulkResult('');
    const failedIds = new Set();
    let successCount = 0;
    for (const product of selectedPending) {
      const report = reportedById.get(String(product._id));
      const succeeded = await onMarkRefunded({ order, product, report });
      if (succeeded) successCount += 1;
      else failedIds.add(String(product._id));
    }
    setSelectedRefundProductIds(failedIds);
    setBulkResult(
      failedIds.size > 0
        ? `${successCount} marked refunded; ${failedIds.size} could not be updated.`
        : `${successCount} item${successCount === 1 ? '' : 's'} marked refunded.`
    );
    setBulkMarking(false);
  };

  const toggleEmailProduct = (productId) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      const key = String(productId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSentMessage('');
  };

  const sendGuestEmail = async () => {
    setSendError('');
    setSentMessage('');
    if (selectedProducts.length === 0) {
      setSendError('Select at least one reported missing product.');
      return;
    }
    if (!subject.trim() || !message.trim()) {
      setSendError('Subject and message are required.');
      return;
    }

    setSending(true);
    try {
      const { data } = await request.post(
        `/orders/${order._id}/reported-missing-products/refund-email`,
        {
          productIds: selectedProducts.map((product) => String(product._id)),
          subject: subject.trim(),
          message: message.trim(),
        }
      );
      setSentMessage(`Email sent to ${data?.sentTo || order.guestEmail}.`);
    } catch (err) {
      setSendError(
        err?.response?.data?.message || 'Unable to send the guest email.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label="Close order refund workflow"
        onClick={onClose}
        className="absolute inset-0 bg-gray-950/60 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reported-missing-refund-title"
        className="relative flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-gray-50 shadow-2xl dark:border-gray-700 dark:bg-gray-950"
      >
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-800 dark:bg-gray-900">
          <div>
            <h2 id="reported-missing-refund-title" className="text-lg font-bold text-gray-900 dark:text-white">
              Order #{order.uniqueId} · Missing Product Refunds
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Review every order item, record refunds, and separately email {order.guestEmail || 'the guest'}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.9fr)]">
          <div className="border-b border-gray-200 p-6 dark:border-gray-800 xl:border-b-0 xl:border-r">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">All order items</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Driver-reported items are highlighted.
                </p>
              </div>
              <span className="rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {order.products?.length || 0} items
              </span>
            </div>

            {pendingReportedProducts.length > 0 ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-3 dark:border-emerald-900/60 dark:bg-emerald-900/15">
                <button
                  type="button"
                  onClick={toggleAllPendingRefunds}
                  disabled={bulkMarking}
                  className="text-xs font-semibold text-emerald-700 hover:underline disabled:opacity-60 dark:text-emerald-300"
                >
                  {allPendingSelected ? 'Clear selection' : 'Select all pending'}
                </button>
                <button
                  type="button"
                  onClick={markSelectedRefunded}
                  disabled={bulkMarking || selectedRefundProductIds.size === 0}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiCheckCircle className="h-4 w-4" />
                  {bulkMarking
                    ? 'Marking selected…'
                    : `Mark selected refunded (${selectedRefundProductIds.size})`}
                </button>
                {bulkResult ? (
                  <div className="w-full text-xs text-emerald-700 dark:text-emerald-300">
                    {bulkResult}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-3">
              {(order.products || []).map((product) => {
                const report = reportedById.get(String(product._id));
                const isMissing = Boolean(report);
                const processing =
                  processingKey === `${order._id}-${product._id}`;
                return (
                  <div
                    key={product._id}
                    className={`rounded-2xl border p-4 ${
                      isMissing
                        ? 'border-rose-300 bg-rose-50/70 dark:border-rose-900/70 dark:bg-rose-900/15'
                        : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white dark:bg-gray-800">
                        {product.image ? (
                          <img src={product.image} alt="" className="h-full w-full object-contain" />
                        ) : (
                          <FiPackage className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {product.name || 'Item'}
                            </div>
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Qty {Number(product.quantity) || 1} · {currency(Number(product.price) || 0)} each
                            </div>
                          </div>
                          <div className="font-bold text-gray-900 dark:text-white">
                            {currency((Number(product.price) || 0) * (Number(product.quantity) || 1))}
                          </div>
                        </div>

                        {isMissing ? (
                          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <div className="text-xs font-semibold text-rose-700 dark:text-rose-300">
                                Driver reported missing
                              </div>
                              <div className="text-[11px] text-rose-600/80 dark:text-rose-300/80">
                                {report.reason || 'Missing or unavailable'}
                              </div>
                            </div>
                            {product.refunded === true ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                <FiCheckCircle className="h-3.5 w-3.5" /> Refunded
                              </span>
                            ) : (
                              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-gray-900 dark:text-emerald-300">
                                <input
                                  type="checkbox"
                                  checked={selectedRefundProductIds.has(String(product._id))}
                                  onChange={() => toggleRefundProduct(product._id)}
                                  disabled={bulkMarking || processing}
                                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                {processing ? 'Saving…' : 'Select for refund'}
                              </label>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-5 bg-white p-6 dark:bg-gray-900">
            <div>
              <div className="flex items-center gap-2">
                <FiMail className="h-4 w-4 text-rose-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Guest email</h3>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Sending this email is separate from refund tracking and does not call Stripe.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                Subject
              </label>
              <input
                type="text"
                maxLength={160}
                value={subject}
                onChange={(event) => {
                  setSubject(event.target.value.replace(/[\r\n]/g, ''));
                  setSentMessage('');
                }}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                Message
              </label>
              <textarea
                rows={8}
                maxLength={5000}
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  setSentMessage('');
                }}
                className="w-full resize-y rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm leading-6 text-gray-900 focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
              />
              <div className="mt-1 text-right text-[10px] text-gray-400">{message.length}/5000</div>
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
                Products shown in email
              </div>
              <div className="space-y-2">
                {reportedProducts.map((product) => (
                  <label
                    key={product._id}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedProductIds.has(String(product._id))}
                        onChange={() => toggleEmailProduct(product._id)}
                        className="h-4 w-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400"
                      />
                      <span className="truncate text-gray-800 dark:text-gray-200">{product.name}</span>
                    </span>
                    <span className="whitespace-nowrap font-semibold text-gray-700 dark:text-gray-300">
                      {currency((Number(product.price) || 0) * (Number(product.quantity) || 1))}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-rose-200 bg-gradient-to-b from-white to-rose-50 p-5 dark:border-rose-900/50 dark:from-gray-950 dark:to-rose-950/20">
              <div className="text-center text-xs font-bold uppercase tracking-wider text-rose-500">Email preview</div>
              <div className="mt-3 text-center text-lg font-bold text-gray-900 dark:text-white">We’re sincerely sorry.</div>
              <div className="mt-4 whitespace-pre-wrap text-sm leading-6 text-gray-600 dark:text-gray-300">{message}</div>
              <div className="mt-5 space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                {selectedProducts.map((product) => (
                  <div key={product._id} className="flex justify-between gap-3 text-sm">
                    <span className="text-gray-700 dark:text-gray-300">
                      {product.name} · Qty {Number(product.quantity) || 1}
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {currency((Number(product.price) || 0) * (Number(product.quantity) || 1))}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-gray-200 pt-3 font-bold text-rose-700 dark:border-gray-700 dark:text-rose-300">
                  <span>Total refunded</span>
                  <span>{currency(selectedTotal)}</span>
                </div>
              </div>
            </div>

            {sendError ? (
              <div className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                {sendError}
              </div>
            ) : null}
            {sentMessage ? (
              <div className="rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                {sentMessage}
              </div>
            ) : null}

            <button
              type="button"
              onClick={sendGuestEmail}
              disabled={sending || selectedProducts.length === 0 || !subject.trim() || !message.trim()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-rose-500 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/20 hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiMail className="h-4 w-4" />
              {sending ? 'Sending…' : `Send email to ${order.guestEmail || 'guest'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
